import { Vue, Component, Ref, Prop, Emit, Provide } from 'vue-property-decorator'
import { differenceBy } from 'lodash'
import {
    ConfigurationIncidentsOriginsContactPersonnelRelationships,
    ConfigurationIncidentsOriginsModalActiveTab,
    ConfigurationIncidentsOriginsModalState,
    initialConfigurationIncidentsOriginsModalState
} from '@/modules/configurationModule/detail/incidents/incidents-origins/components/incidents-origins-modal/incidents-origins-modal.model'
import IncidentOriginsConfigurationServices from '@/api/resources/configuration/incidents/origins/IncidentOriginsConfigurationServices'
import { personnelService } from '@/api/resources/personnelService'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import { incidentsOriginsLaboratoriesPersonnelRelationship } from '@/modules/configurationModule/detail/incidents/incidents-origins/services/incidents-origins-laboratories-personnel-relationship.service'
import {
    ConfigurationIncidentOriginLaboratoryDefaultPersonnelRequest,
    ConfigurationIncidentOriginLaboratoryDefaultPersonnelResponse
} from '@/modules/configurationModule/detail/incidents/incidents-origins/services/incidents-origins-default-personnel-relationship.dto'
import {
    ConfigurationIncidentsOriginsLaboratoriesCreateRequest,
    ConfigurationIncidentsOriginsLaboratoriesPatchInBatchRequest,
    ConfigurationIncidentsOriginsLaboratoriesResponse,
    IncidentOriginResponse
} from '@/modules/configurationModule/detail/incidents/incidents-origins/services/incidents-origins-laboratories.dto'
import { Nullable } from '@/shared/types/app.types'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'

@Component
export default class IncidentsOriginsModal extends Vue {
    @Ref() readonly incidentsOriginsModal!: any

    @Prop(Boolean) readonly isGlobal!: boolean

    @Provide() externalValidator = this.$validator

    @Emit()
    update(): ConfigurationIncidentsOriginsLaboratoriesPatchInBatchRequest[] {
        return [{
            active: this.state.basicData.active,
            id: this.state.id
        }]
    }

    @Emit()
    create(): ConfigurationIncidentsOriginsLaboratoriesCreateRequest {
        return {
            active: this.state.basicData.active,
            incidentOriginId: this.state.basicData.incidentOrigin?.id
        }
    }

    state: ConfigurationIncidentsOriginsModalState

    get laboratoryId(): number {
        return this.$store.getters.labId
    }

    get actionTitle(): string {
        return this.state.id ? this.$t('configuration.actions.update').toString()
            : this.$t('configuration.actions.add').toString()
    }

    get filteredContactPersonnel(): ConfigurationIncidentsOriginsContactPersonnelRelationships[] {
        return this.state.contactPersonnel.filter(personnel => personnel.fullName.includes(this.state.contactPersonnelFilter))
    }

    get disabledContactPersonnelTab(): boolean {
        return !this.state.id
    }

    get disabledIncidentOriginCombo(): boolean {
        return !!this.state.id
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    constructor() {
        super()
        this.state = initialConfigurationIncidentsOriginsModalState()
    }

    async show(selectedRow: Nullable<ConfigurationIncidentsOriginsLaboratoriesResponse>): Promise<void> {
        await this.initializeIncidentOrigin(selectedRow)
        this._showModal()
    }

    private _showModal(): void {
        this.incidentsOriginsModal.show()
    }

    async initializeIncidentOrigin(incidentOrigins: Nullable<ConfigurationIncidentsOriginsLaboratoriesResponse>): Promise<void> {
        this.state = initialConfigurationIncidentsOriginsModalState()
        await this.$validator.reset()
        if (incidentOrigins) {
            this.state = {
                id: incidentOrigins.id,
                activeTabIndex: ConfigurationIncidentsOriginsModalActiveTab.BASIC_DATA,
                contactPersonnelFilter: '',
                basicData: {
                    incidentOrigin: incidentOrigins.incidentOrigin,
                    active: incidentOrigins.active
                },
                contactPersonnel: await this.setIncidentOriginsContactPersonnel(incidentOrigins.id, incidentOrigins.laboratory.labId)
            }
        }
    }
    async setIncidentOriginsContactPersonnel(incidentOriginLaboratoryId: number, laboratoryId: number): Promise<ConfigurationIncidentsOriginsContactPersonnelRelationships[]> {
        let allLaboratoryPersonnel: CommonPersonnelResponse[] = await this._getPersonnel(laboratoryId)
        const incidentOriginDefaultPersonnelRelationships = await this._getDefaultPersonnelRelationships(laboratoryId, incidentOriginLaboratoryId)
        const incidentOriginDefaultPersonnel = incidentOriginDefaultPersonnelRelationships.map(personnelRelationship => personnelRelationship.personnel)
        const assignedPersonnelList = differenceBy(incidentOriginDefaultPersonnel, allLaboratoryPersonnel)
        return allLaboratoryPersonnel.map(personnel => {
            const personnelRelationship = incidentOriginDefaultPersonnelRelationships.find(personnelRelationship => personnelRelationship.personnel.id === personnel.id)
            return {
                active: !!assignedPersonnelList.find(assignedPersonnel => assignedPersonnel.id === personnel.id),
                personnelId: personnel.id,
                fullName: personnel.fullName,
                incidentOriginLaboratoryDefaultPersonnelId: personnelRelationship?.id
            }
        })
    }

    async getAllIncidentOrigins(search: Nullable<string>): Promise<IncidentOriginResponse[]> {
        const filter: CommonFilterModel = {
            'name': GridUtils.getTextFilterModel('contains', search)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response =
            await PromiseUtils.toCallSecure(
                IncidentOriginsConfigurationServices.paginator(filter, params))
        return response?.content ?? []
    }

    private async _getDefaultPersonnelRelationships(laboratoryId: number, incidentOriginLaboratoryId: number): Promise<ConfigurationIncidentOriginLaboratoryDefaultPersonnelResponse[]> {
        const [err, response] = await this.$_toCall(incidentsOriginsLaboratoriesPersonnelRelationship.getIncidentsOriginsLaboratoryDefaultPersonnelRelationships(laboratoryId, incidentOriginLaboratoryId), true)
        if (err) {
            return []
        }
        return response
    }

    private async _createIncidentsOriginsLaboratoryDefaultPersonnel(laboratoryId: number, personnelRelationship: ConfigurationIncidentsOriginsContactPersonnelRelationships): Promise<void> {
        const createRequest: ConfigurationIncidentOriginLaboratoryDefaultPersonnelRequest = {
            personnelId: personnelRelationship.personnelId,
            incidentOriginLaboratoryId: this.state.id
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(incidentsOriginsLaboratoriesPersonnelRelationship.createIncidentsOriginsLaboratoryDefaultPersonnel(laboratoryId, createRequest), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        personnelRelationship.incidentOriginLaboratoryDefaultPersonnelId = response.id
        this.$_showToastSuccess(this.$t('configuration.toastMessages.incidentOriginContactPersonnelAssociationCreated').toString())
    }

    private async _deleteIncidentsOriginsLaboratoryDefaultPersonnel(laboratoryId: number, incidentOriginLaboratoryDefaultPersonnelId: Nullable<number>): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsOriginsLaboratoriesPersonnelRelationship.deleteIncidentsOriginsLaboratoryDefaultPersonnel(laboratoryId, incidentOriginLaboratoryDefaultPersonnelId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.incidentOriginContactPersonnelAssociationDeleted').toString())
    }

    private async _getPersonnel(laboratoryId: number): Promise<CommonPersonnelResponse[]> {
        const [err, response] = await this.$_toCall(personnelService.getPersonnel(laboratoryId, null), true)
        if (err) {
            return []
        }
        return response
    }

    actionModal(): void {
        if (this.state.id) {
            this.update()
        } else {
            this.create()
        }
    }

    async onContactPersonnelCheckboxChange(personnelRelationship: ConfigurationIncidentsOriginsContactPersonnelRelationships): Promise<void> {
        if (!personnelRelationship.active) {
            await this._deleteIncidentsOriginsLaboratoryDefaultPersonnel(this.laboratoryId, personnelRelationship.incidentOriginLaboratoryDefaultPersonnelId)
        } else {
            await this._createIncidentsOriginsLaboratoryDefaultPersonnel(this.laboratoryId, personnelRelationship)
        }
    }

    hide(): void {
        this.incidentsOriginsModal.hide()
    }
}
